import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { TextInput, PasswordInput } from "@carbon/react";

type CarbonTextInputProps = ComponentPropsWithoutRef<typeof TextInput>;

export type TextFieldProps<TFieldValues extends FieldValues> = Omit<
  CarbonTextInputProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "name"
  | "onChange"
  | "onBlur"
  | "value"
  | "defaultValue"
  | "ref"
> & {
  /** The RHF field name — also becomes the input's `id`, which is what
   * makes `getByLabelText`/`getByRole(..., { name })` resolve. */
  name: Path<TFieldValues>;
  /** The field's visible label (already translated by the caller, the
   * same convention as `PageHeader`'s `title`). */
  label: string;
  control: Control<TFieldValues>;
  /** Appends the translated `form.optional` marker ("(optional)") to `label`. See this
   * component's docstring for when to reach for it instead of `required`. */
  optional?: boolean;
};

/**
 * A Carbon `TextInput` bound to a React Hook Form field via `Controller`.
 * Renders `invalid`/`invalidText` straight from `fieldState.error`, so a
 * Yup error shows up the moment RHF validates the field — no consumer-side
 * wiring beyond passing `control`.
 *
 * Every other `TextInput` prop (`type`, `placeholder`, `disabled`,
 * `helperText`, `size`, ...) passes through unchanged, so this covers any
 * text-shaped field a form needs rather than only the ones Users happens
 * to use first.
 *
 * Accessibility guarantee this primitive exists to make cheap: the label
 * is a real `<label htmlFor>` tied to the input's `id` (`getByLabelText`
 * and `getByRole(..., { name: label })` both resolve), and an invalid
 * field carries `aria-invalid` plus Carbon's own `aria-errormessage`
 * pointing at the rendered `invalidText` — a screen reader announces the
 * real error, not just text sitting near the input.
 *
 * `optional`: a purely visual gap the Carbon rebuild otherwise leaves open. Screen readers are
 * unaffected by it — `required` falls straight through to the native element and the browser
 * maps it into the accessibility tree regardless of what's painted — but sighted users lost the
 * pre-Carbon forms' hand-rolled red asterisk next to a required label, because neither
 * `@carbon/react` nor `@carbon/styles` ships a required-marker component, prop, or CSS class at
 * all (confirmed by reading both: the only `required` mention in the styles is a Firefox fix
 * that removes a red validity ring, nothing that paints a marker).
 *
 * Carbon's own usage guidance for `TextInput` supplies the convention instead of an API: "If the
 * majority of the fields are required, mark only the optional field labels with (optional). If
 * the majority of the fields are optional, mark only the required field labels with (required)."
 * It is deliberately NOT a required-field asterisk — which direction you mark depends on which
 * case is the minority on that specific form.
 *
 * This prop only implements the `(optional)` half: every form in this codebase so far is
 * majority-required, so every marked field here is one of the minority optional ones. There is
 * no `required`-marking counterpart — nothing here needs it yet. A future majority-*optional*
 * form needs the other direction: mark the (minority) REQUIRED fields' labels with "(required)"
 * instead, via the same append pattern, and leave the majority optional fields unmarked.
 */
function TextField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  optional,
  ...rest
}: TextFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const labelText = optional ? `${label} ${t("form.optional")}` : label;

  // A password field renders Carbon's `PasswordInput` (a `TextInput` with a built-in show/hide
  // toggle) instead of a plain `TextInput`, so every password field in the app — login, register,
  // new-password — gets the visibility toggle for free. It still renders `<input type="password">`
  // while hidden, so the label association and the input's role are unchanged. `type` is dropped
  // from the passthrough here: `PasswordInput` owns its own `password`/`text` state via the toggle,
  // and its narrower `type` prop is incompatible with `TextInput`'s broad `string` type.
  const { type, ...restWithoutType } = rest;
  const isPassword = type === "password";

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) =>
        isPassword ? (
          <PasswordInput
            {...restWithoutType}
            {...field}
            id={name}
            labelText={labelText}
            showPasswordLabel={t("form.showPassword")}
            hidePasswordLabel={t("form.hidePassword")}
            tooltipPosition="left"
            invalid={Boolean(fieldState.error)}
            invalidText={fieldState.error?.message}
          />
        ) : (
          <TextInput
            {...rest}
            {...field}
            id={name}
            labelText={labelText}
            invalid={Boolean(fieldState.error)}
            invalidText={fieldState.error?.message}
          />
        )
      }
    />
  );
}

export default TextField;
